import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Switch,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, ConfirmationModal } from '../components';
import { Button } from '../components/design-system';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';

// ─── Type config ──────────────────────────────────────────────────────────────

const TYPE_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
  flat_rate:         { label: 'Flat Rate',        icon: 'pricetag-outline',    color: '#4A90E2' },
  free:              { label: 'Free',              icon: 'gift-outline',        color: '#50C878' },
  location_based:    { label: 'Zone-Based',        icon: 'map-outline',         color: '#E8624A' },
  local_pickup:      { label: 'Local Pickup',      icon: 'storefront-outline',  color: '#FF9500' },
  external_provider: { label: 'Carrier / Provider',icon: 'airplane-outline',    color: '#7B68EE' },
  online_delivery:   { label: 'Online Delivery',   icon: 'cloud-download-outline', color: '#00BCD4' },
  custom:            { label: 'Custom',            icon: 'options-outline',     color: '#9E9E9E' },
};

const FILTER_TYPES = ['all', 'flat_rate', 'free', 'location_based', 'local_pickup', 'external_provider', 'online_delivery', 'custom'] as const;

// ─── Card ─────────────────────────────────────────────────────────────────────

const ShippingMethodCard: React.FC<{
  method: any;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: (value: boolean) => void;
  toggling: boolean;
}> = ({ method, onEdit, onDelete, onToggleActive, toggling }) => {
  const styles = useThemedStyles(getStyles);
  const cfg = TYPE_CONFIG[method.type] || TYPE_CONFIG.custom;

  const priceLabel = method.type === 'free'
    ? 'Free'
    : method.type === 'location_based'
    ? `${(method.zones || []).length} zone${(method.zones || []).length !== 1 ? 's' : ''}`
    : method.price === 0 ? 'Free' : `${method.price.toFixed(2)}`;

  const daysLabel = method.estimatedDays
    ? `${method.estimatedDays.min}–${method.estimatedDays.max} days`
    : null;

  return (
    <View style={[styles.card, !method.isActive && styles.cardInactive]}>
      {/* Icon + badge */}
      <View style={[styles.cardIconWrap, { backgroundColor: cfg.color + '18' }]}>
        <Ionicons name={cfg.icon as any} size={22} color={cfg.color} />
      </View>

      {/* Body */}
      <View style={styles.cardBody}>
        <View style={styles.cardTitleRow}>
          <Text style={styles.cardName} numberOfLines={1}>{method.name}</Text>
          <View style={[styles.typeBadge, { backgroundColor: cfg.color + '20' }]}>
            <Text style={[styles.typeBadgeText, { color: cfg.color }]}>{cfg.label}</Text>
          </View>
        </View>

        {method.description ? (
          <Text style={styles.cardDesc} numberOfLines={1}>{method.description}</Text>
        ) : null}

        <View style={styles.cardMeta}>
          <Text style={styles.cardPrice}>{priceLabel}</Text>
          {daysLabel ? <Text style={styles.cardDays}> · {daysLabel}</Text> : null}
          {method.providerName ? <Text style={styles.cardProvider}> · {method.providerName}</Text> : null}
        </View>

        {method.availableFor?.length ? (
          <View style={styles.availableRow}>
            {method.availableFor.map((t: string) => (
              <View key={t} style={styles.availableChip}>
                <Text style={styles.availableChipText}>{t}</Text>
              </View>
            ))}
          </View>
        ) : null}
      </View>

      {/* Actions */}
      <View style={styles.cardActions}>
        {toggling ? (
          <ActivityIndicator size="small" color={colors.primary} style={styles.toggleLoader} />
        ) : (
          <Switch
            value={method.isActive}
            onValueChange={onToggleActive}
            trackColor={{ false: colors.border, true: colors.primary + '60' }}
            thumbColor={method.isActive ? colors.primary : colors.textLight}
            style={styles.toggle}
          />
        )}
        <TouchableOpacity onPress={onEdit} style={styles.actionBtn} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Ionicons name="pencil-outline" size={18} color={colors.textLight} />
        </TouchableOpacity>
        <TouchableOpacity onPress={onDelete} style={styles.actionBtn} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Ionicons name="trash-outline" size={18} color={colors.error} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

// ─── Screen ───────────────────────────────────────────────────────────────────

export const ShippingMethodsScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();

  const [methods, setMethods] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async (reset = false) => {
    try {
      setError(null);
      if (reset) setLoading(true);
      const query = filterType !== 'all' ? { type: filterType, limit: 200 } : { limit: 200 };
      const res = await apiService.getShippingMethods(query);
      if (res.statusCode === 1000 && res.data) {
        setMethods(res.data.methods || []);
        setTotal(res.data.pagination?.total ?? res.data.methods?.length ?? 0);
      } else {
        setError(res.errors?.[0]?.message || 'Failed to load shipping methods');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load shipping methods');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filterType]);

  useEffect(() => { load(true); }, [load]);

  const handleRefresh = () => { setRefreshing(true); load(false); };

  const handleToggleActive = async (method: any) => {
    try {
      setTogglingId(method._id);
      const res = await apiService.updateShippingMethod(method._id, { isActive: !method.isActive });
      if (res.statusCode === 1000) {
        setMethods(prev => prev.map(m => m._id === method._id ? { ...m, isActive: !m.isActive } : m));
      } else {
        showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to update');
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message);
    } finally {
      setTogglingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!methodToDelete) return;
    try {
      setDeleting(true);
      const res = await apiService.deleteShippingMethod(methodToDelete);
      if (res.statusCode === 1000) {
        showNotification('success', 'Deleted', 'Shipping method removed');
        setShowDeleteModal(false);
        setMethodToDelete(null);
        load(false);
      } else {
        showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to delete');
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message);
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const renderListContent = () => {
    if (loading && methods.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.centerText}>Loading shipping methods...</Text>
        </View>
      );
    }

    if (error && methods.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="alert-circle-outline" size={64} color={colors.error} />
          <Text style={styles.errorTitle}>Unable to Load</Text>
          <Text style={styles.errorText}>{error}</Text>
          <Button title="Try Again" onPress={() => load(true)} variant="primary" style={styles.retryBtn} />
        </View>
      );
    }

    if (methods.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="car-outline" size={64} color={colors.border} />
          <Text style={styles.errorTitle}>No shipping methods</Text>
          <Text style={styles.errorText}>
            Add flat rate, free, local pickup, carrier, or online delivery options for your orders.
          </Text>
          <Button
            title="Add Shipping Method"
            onPress={() => navigateTo('shipping-method-create')}
            variant="primary"
            style={styles.retryBtn}
          />
        </View>
      );
    }

    return (
      <FlatList
        data={methods}
        keyExtractor={(item) => item._id}
        renderItem={({ item }) => (
          <ShippingMethodCard
            method={item}
            onEdit={() => navigateTo('shipping-method-edit', item._id)}
            onDelete={() => { setMethodToDelete(item._id); setShowDeleteModal(true); }}
            onToggleActive={() => handleToggleActive(item)}
            toggling={togglingId === item._id}
          />
        )}
        contentContainerStyle={styles.listContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.primary} />}
        ItemSeparatorComponent={() => <View style={styles.separator} />}
      />
    );
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
    >
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.pageHeader}>
          <View style={styles.pageHeaderContent}>
            <View style={styles.pageHeaderLeft}>
              <Text style={styles.pageTitle}>Shipping Methods</Text>
              {!loading && (
                <Text style={styles.pageSubtitle}>
                  {total} {total === 1 ? 'method' : 'methods'} configured
                </Text>
              )}
            </View>
            <TouchableOpacity
              style={styles.addButton}
              onPress={() => navigateTo('shipping-method-create')}
              activeOpacity={0.8}
            >
              <Ionicons name="add" size={24} color={colors.white} />
            </TouchableOpacity>
          </View>
          <Text style={styles.pageHint}>
            Define how orders are shipped — flat rate, free, carrier pickup, or digital delivery.
          </Text>

          {/* Filter chips */}
          <FlatList
            horizontal
            showsHorizontalScrollIndicator={false}
            data={FILTER_TYPES}
            keyExtractor={(t) => t}
            contentContainerStyle={styles.filterRow}
            renderItem={({ item: t }) => {
              const cfg = t === 'all' ? null : TYPE_CONFIG[t];
              const active = filterType === t;
              return (
                <TouchableOpacity
                  style={[styles.filterChip, active && styles.filterChipActive]}
                  onPress={() => setFilterType(t)}
                  activeOpacity={0.7}
                >
                  {cfg ? (
                    <Ionicons name={cfg.icon as any} size={13} color={active ? colors.white : cfg.color} style={{ marginRight: 3 }} />
                  ) : (
                    <Ionicons name="apps-outline" size={13} color={active ? colors.white : colors.textLight} style={{ marginRight: 3 }} />
                  )}
                  <Text style={[styles.filterChipText, active && styles.filterChipTextActive]}>
                    {t === 'all' ? 'All' : cfg?.label}
                  </Text>
                </TouchableOpacity>
              );
            }}
          />
        </View>

        <View style={styles.listContainer}>{renderListContent()}</View>
      </View>

      <ConfirmationModal
        visible={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setMethodToDelete(null); }}
        onConfirm={confirmDelete}
        title="Remove Shipping Method"
        message="This will remove the shipping method. Existing orders that used it will keep their snapshot."
        confirmText="Remove"
        cancelText="Cancel"
        type="error"
        loading={deleting}
      />
    </ScreenLayout>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.lightGray },
  pageHeader: {
    backgroundColor: colors.background.surface,
    paddingTop: spacing.lg,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    ...designTokens.shadows.sm,
  },
  pageHeaderContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  pageHeaderLeft: { flex: 1 },
  pageTitle: {
    fontSize: typography.fontSize.xxl,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
    marginBottom: spacing.xs,
  },
  pageSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  pageHint: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginTop: spacing.xs,
    marginBottom: spacing.sm,
  },
  addButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    ...designTokens.shadows.sm,
  },
  filterRow: {
    paddingVertical: spacing.xs,
    gap: spacing.xs,
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
    marginRight: spacing.xs,
  },
  filterChipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  filterChipText: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.medium,
  },
  filterChipTextActive: {
    color: colors.white,
  },
  listContainer: { flex: 1 },
  listContent: {
    padding: spacing.md,
    paddingBottom: Platform.OS !== 'web' ? 100 : spacing.lg,
  },
  separator: { height: spacing.sm },
  // ─── Card ─────────────────────────────────────────────────────────────────
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.md,
    gap: spacing.md,
    ...designTokens.shadows.sm,
  },
  cardInactive: { opacity: 0.55 },
  cardIconWrap: {
    width: 44,
    height: 44,
    borderRadius: designTokens.borderRadius.md,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardBody: { flex: 1, minWidth: 0 },
  cardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexWrap: 'wrap',
    marginBottom: 2,
  },
  cardName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    flexShrink: 1,
  },
  typeBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  typeBadgeText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
  },
  cardDesc: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginBottom: 2,
  },
  cardMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  cardPrice: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  cardDays: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  cardProvider: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  availableRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 4,
  },
  availableChip: {
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 6,
    backgroundColor: colors.lightGray,
  },
  availableChipText: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  cardActions: {
    alignItems: 'center',
    gap: spacing.xs,
  },
  toggle: { transform: [{ scaleX: 0.85 }, { scaleY: 0.85 }] },
  toggleLoader: { width: 40 },
  actionBtn: { padding: spacing.xs },
  // ─── States ───────────────────────────────────────────────────────────────
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxl,
  },
  centerText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  errorTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: '600',
    color: colors.textDark,
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
    fontFamily: typography.fontFamily.semibold,
    textAlign: 'center',
  },
  errorText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    textAlign: 'center',
    marginBottom: spacing.xl,
    fontFamily: typography.fontFamily.regular,
  },
  retryBtn: { minWidth: 180 },
});
